import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { EntryPath } from '@/Config/app/router/entry.path';
import { ReCaptchaCheckbox } from '@/Domain/components/Auth/ReCaptchaCheckbox';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { useResetPassword } from '@/Domain/hooks/Auth/useResetPassword.hook';

export const ResetPasswordPage = () => {
  const navigate = useNavigate();

  const { t } = useTranslation(['reset-password', 'reset_password_success']);
  const { track } = useAnalytics();

  const {
    onVerify,
    isPending,
    isSuccess,
    onSubmit,
    handleSubmit,
    errors,
    register,
    useV2,
    control,
  } = useResetPassword();

  const handleSuccess = () => {
    track('rest_password_back_success_click', {
      event_category: 'reset_password_form',
      event_label: 'User reset password',
    });

    navigate(EntryPath.login, { viewTransition: true });
  };

  const handleBackTrack = () =>
    track('rest_password_back', {
      event_category: 'reset_password_form',
      event_label: 'User back to login',
    });

  if (isSuccess) {
    return (
      <>
        <h1 className="font-semibold mb-8 text-xl text-center text-black">
          {t('reset_password_success:title')}
        </h1>
        <p className="text-base">{t('reset_password_success:description')}</p>
        <div className="flex mt-4 justify-center">
          <Button variant="dark" className="w-full" onClick={handleSuccess}>
            {t('reset_password_success:link')}
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="font-semibold mb-8 text-xl text-center text-black">
        {t('reset-password:title')}
      </div>
      {errors.token && (
        <div className="p-4 mb-2 text-red-600 text-sm bg-red-200 border border-red-200 rounded">
          {t('reset-password:validation.invalid_token')}
        </div>
      )}
      <form
        id="register-email-form"
        className="grid space-y-4 mb-3"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div>
          <label htmlFor="password1" className="text-sm text-gray-950">
            {t('reset-password:form.password')}
          </label>
          <Input
            id="password1"
            type="password"
            placeholder={t('reset-password:form.password_placeholder')}
            {...register('new_password1')}
            error={!!errors.new_password1}
          />
          {errors.new_password1 && (
            <span className="text-sm text-red-700">{errors.new_password1.message}</span>
          )}
        </div>

        <div>
          <label htmlFor="password2" className="text-sm text-gray-950">
            {t('form.confirm_password')}
          </label>
          <Input
            id="password2"
            placeholder={t('reset-password:form.password_placeholder')}
            type="password"
            {...register('new_password2')}
            error={!!errors.new_password2}
          />
          {errors.new_password2 && (
            <span className="text-sm text-red-700">{errors.new_password2.message}</span>
          )}
        </div>

        <div>
          <GoogleReCaptcha onVerify={onVerify} />
        </div>
        <Button variant="dark" type="submit" disabled={isPending}>
          {isPending ? <Spinner /> : t('reset-password:button.submit')}
        </Button>
        {useV2 && (
          <Controller
            name="recaptcha_response_v2"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange }, fieldState: { invalid } }) => (
              <ReCaptchaCheckbox onChange={onChange} isInvalid={invalid} />
            )}
          />
        )}
      </form>
      <p className="!mt-8 text-center text-sm">
        {t('reset-password:back_to')}{' '}
        <Link className="text-gray-950 font-medium" to="/" onClick={handleBackTrack}>
          {t('reset-password:link.login')}
        </Link>
      </p>
    </>
  );
};

export default ResetPasswordPage;
